/**
 * Desktop only: positions search/cart panels below the triangle caret.
 * Mobile uses centered modal CSS (see editorial-homepage.liquid).
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');
  const GAP_PX = 8;
  const SELECTOR =
    '.editorial-homepage__utility--search, .editorial-homepage__utility--cart, .editorial-homepage__utility--language';

  /**
   * @param {HTMLElement} utility
   * @returns {HTMLElement | null}
   */
  function getPopover(utility) {
    const inUtility = utility.querySelector('.editorial-homepage__popover');
    if (inUtility instanceof HTMLElement) return inUtility;

    const portaled = document.querySelector(
      `.editorial-homepage__popover--portaled[data-editorial-utility-id="${utility.dataset.editorialUtilityId}"]`
    );
    return portaled instanceof HTMLElement ? portaled : null;
  }

  /**
   * @param {HTMLElement} popover
   */
  function clearMobileInlinePosition(popover) {
    popover.style.top = '';
    popover.style.left = '';
    popover.style.removeProperty('--editorial-panel-anchor-top');
  }

  /**
   * @param {HTMLElement} utility
   */
  function syncCartPanelViewportHeight(utility) {
    if (!utility.classList.contains('editorial-homepage__utility--cart')) {
      return;
    }

    const popover = getPopover(utility);
    if (!(popover instanceof HTMLElement)) {
      return;
    }

    const viewport = window.visualViewport;
    const viewportHeight = viewport ? viewport.height : window.innerHeight;
    const bottomGap = 16;
    let maxHeight;

    if (MOBILE_MQ.matches) {
      popover.style.removeProperty('--editorial-cart-popover-max-height');
      return;
    }

    const top = popover.getBoundingClientRect().top;
    maxHeight = Math.max(220, Math.floor(viewportHeight - top - bottomGap));

    popover.style.setProperty('--editorial-cart-popover-max-height', `${maxHeight}px`);
  }

  /**
   * @param {HTMLElement} utility
   */
  function syncPanelAnchorTop(utility) {
    const popover = getPopover(utility);
    if (!popover) return;

    if (MOBILE_MQ.matches) {
      clearMobileInlinePosition(popover);
      syncCartPanelViewportHeight(utility);
      return;
    }

    const anchor =
      utility.querySelector('.editorial-homepage__action-caret') ||
      utility.querySelector('.editorial-homepage__action-stack') ||
      utility.querySelector('.editorial-homepage__action');

    if (!(anchor instanceof HTMLElement)) return;

    const rect = anchor.getBoundingClientRect();
    if (rect.bottom <= 0 && rect.top <= 0) return;

    const topPx = Math.round(rect.bottom + GAP_PX);
    popover.style.top = `${topPx}px`;
    popover.style.setProperty('--editorial-panel-anchor-top', `${topPx}px`);

    const root = utility.closest('.editorial-homepage');
    if (root instanceof HTMLElement) {
      root.style.setProperty('--editorial-panel-anchor-top', `${topPx}px`);
      const panelLeft = getComputedStyle(root).getPropertyValue('--editorial-panel-left').trim();
      if (panelLeft) {
        popover.style.setProperty('--editorial-panel-left', panelLeft);
      }
    }

    syncCartPanelViewportHeight(utility);
  }

  /**
   * @param {HTMLElement} utility
   */
  function scheduleSync(utility) {
    syncPanelAnchorTop(utility);
    requestAnimationFrame(() => {
      syncPanelAnchorTop(utility);
      requestAnimationFrame(() => syncPanelAnchorTop(utility));
    });
  }

  /**
   * @param {HTMLElement} utility
   */
  function initUtility(utility) {
    if (!(utility instanceof HTMLElement)) return;

    if (!utility.dataset.editorialUtilityId) {
      utility.dataset.editorialUtilityId = `utility-${Math.random().toString(36).slice(2, 9)}`;
    }

    const popover = utility.querySelector('.editorial-homepage__popover');
    if (popover instanceof HTMLElement) {
      popover.dataset.editorialUtilityId = utility.dataset.editorialUtilityId;
    }

    if (utility.dataset.popoverClampInit === 'true') {
      return;
    }
    utility.dataset.popoverClampInit = 'true';

    const runSync = () => scheduleSync(utility);

    utility.addEventListener('mouseenter', runSync);
    utility.addEventListener('focusin', runSync);

    const popoverEl = utility.querySelector('.editorial-homepage__popover');
    if (popoverEl) {
      popoverEl.addEventListener('mouseenter', runSync);

      const popoverObserver = new MutationObserver(() => {
        if (MOBILE_MQ.matches) {
          clearMobileInlinePosition(popoverEl);
          return;
        }
        if (popoverEl.classList.contains('is-open')) {
          runSync();
        }
      });
      popoverObserver.observe(popoverEl, { attributes: true, attributeFilter: ['class'] });
    }

    const utilityObserver = new MutationObserver(() => {
      if (MOBILE_MQ.matches) {
        if (popoverEl instanceof HTMLElement) {
          clearMobileInlinePosition(popoverEl);
        }
        return;
      }
      if (
        utility.classList.contains('is-mobile-popover-open') ||
        (popoverEl instanceof HTMLElement && popoverEl.classList.contains('is-open'))
      ) {
        runSync();
      }
    });
    utilityObserver.observe(utility, { attributes: true, attributeFilter: ['class'] });

    const onMqChange = () => {
      if (MOBILE_MQ.matches && popoverEl instanceof HTMLElement) {
        clearMobileInlinePosition(popoverEl);
      } else {
        runSync();
      }
    };

    if (typeof MOBILE_MQ.addEventListener === 'function') {
      MOBILE_MQ.addEventListener('change', onMqChange);
    } else if (typeof MOBILE_MQ.addListener === 'function') {
      MOBILE_MQ.addListener(onMqChange);
    }

    window.addEventListener('resize', runSync, { passive: true });
    window.addEventListener('scroll', runSync, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', runSync, { passive: true });
      window.visualViewport.addEventListener('scroll', runSync, { passive: true });
    }

    document.addEventListener('editorial:popover-position', (event) => {
      if (!(event instanceof CustomEvent)) return;
      if (event.detail?.utility === utility) {
        runSync();
      }
    });

    runSync();
  }

  function initPopoverClamp() {
    document.querySelectorAll(SELECTOR).forEach(initUtility);
  }

  window.EditorialPopoverPosition = {
    sync: syncPanelAnchorTop,
    schedule: scheduleSync,
    syncCartHeight: syncCartPanelViewportHeight,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPopoverClamp);
  } else {
    initPopoverClamp();
  }

  document.addEventListener('shopify:section:load', initPopoverClamp);
})();
